import { useCallback, useEffect, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import { cartEditedAtom, v2CartAtom } from '@/core/cart/cart.store'
import { fgProductsV2Atom } from '@/core/feelgreat/feelgreat.store'
import { checkoutUuidAtom } from '@/core/checkout/checkout.store'
import { useCartLimit } from '@/core/cart/useCartLimit'
import { omanFormatDataAtom, isLoadingOmanFormatAtom, omanCartSyncingAtom, omanSelectedShippingMethodAtom, omanDeliveryFreightAtom } from '../stores/oman.store'
import { updateOmanCartQty, getOmanFormat } from '../services/oman.api'
import { getHydraFreightAmount } from '../utils/oman-format.mapper'
import { isOmanUuidExpiredError } from '../utils/oman-error.helper'
import { analytics } from '@/core/analytics/analytics'
import type { FGProductV2 } from '@/core/feelgreat/feelgreat.types'
import type { V2CartItem } from '@/core/cart/cart.store'

const findV2IndexByItemCode = (v2Cart: V2CartItem[], productsV2: FGProductV2[], itemCode: string): number => {
    return v2Cart.findIndex(item => {
        const product = productsV2.find(p => p.product_id === item.product_id)
        if (!product) return false
        const matchingVariant = product.variants.find(variant => Object.keys(variant.combination).every(k => item.selection[k] === variant.combination[k]))
        if (!matchingVariant) return false
        return matchingVariant.optionItems.some(oi => oi.item_code === itemCode && oi.option === item.purchase_option)
    })
}

const OMAN_CURRENCY = 'OMR'
const SYNC_DEBOUNCE_MS = 1000

/**
 * Oman cart actions — V2 only.
 *
 * Manages `v2CartAtom` directly (isolated from the generic V1 `cartAtom`).
 * Composes two concerns:
 *
 *   1. **Debounced server sync**: every cart write resets a 1s timer.
 *      Only after the user stops clicking for 1s does it fire
 *      `PUT updateOmanCartQty` → `GET getOmanFormat`.
 *
 *   2. **Format-item handlers**: Oman's order summary renders items from
 *      `formatData.items`. The +/-/remove buttons on those rows translate
 *      `item_code` back to a V2 cart index via `findV2IndexByItemCode`.
 */
export const useOmanCartActions = () => {
    const navigate = useNavigate()
    const [v2Cart, setV2Cart] = useAtom(v2CartAtom)
    const fgProductsV2 = useAtomValue(fgProductsV2Atom)
    const uuid = useAtomValue(checkoutUuidAtom)
    const selectedShippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    const setFormatData = useSetAtom(omanFormatDataAtom)
    const setIsLoadingFormat = useSetAtom(isLoadingOmanFormatAtom)
    const setCartSyncing = useSetAtom(omanCartSyncingAtom)
    const setDeliveryFreight = useSetAtom(omanDeliveryFreightAtom)
    const setCartEdited = useSetAtom(cartEditedAtom)

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const latestPacksRef = useRef<string | null>(null)

    // Cleanup: cancel pending sync and reset flags on unmount so returning to
    // checkout cannot inherit a stale loading state.
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            latestPacksRef.current = null
            setCartSyncing(false)
            setIsLoadingFormat(false)
        },
        [setCartSyncing, setIsLoadingFormat]
    )

    // Debounced sync. Each call resets the timer so batched clicks collapse
    // into a single PUT/GET pair.
    const syncCartToServer = useCallback(
        (shipmentPacks: string) => {
            if (!uuid) return
            if (shipmentPacks === '[]' || shipmentPacks === '{}') return

            latestPacksRef.current = shipmentPacks
            setCartSyncing(true)

            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(async () => {
                const packs = latestPacksRef.current
                if (!packs) {
                    setCartSyncing(false)
                    return
                }

                setIsLoadingFormat(true)
                try {
                    try {
                        await updateOmanCartQty(uuid, packs)
                    } catch {
                        /* PUT failed — cart is correct locally */
                        // Invisible until checkout disagrees with the cart the shopper sees.
                        analytics.track('cartSyncFailed', { op: 'update_qty' })
                    }

                    try {
                        const data = await getOmanFormat(uuid)
                        setFormatData({ ...data, uuid })
                        if (selectedShippingMethod === 'delivery') {
                            setDeliveryFreight(getHydraFreightAmount(data))
                        }
                    } catch (e) {
                        analytics.track('cartSyncFailed', { op: 'get_format', is_uuid_expired: isOmanUuidExpiredError(e) })
                        if (isOmanUuidExpiredError(e)) {
                            navigate('/', { replace: true })
                            return
                        }
                        /* GET failed — keep last known format data */
                    }
                } finally {
                    setIsLoadingFormat(false)
                    setCartSyncing(false)
                }
            }, SYNC_DEBOUNCE_MS)
        },
        [navigate, selectedShippingMethod, setCartSyncing, setDeliveryFreight, setFormatData, setIsLoadingFormat, uuid]
    )

    const applyV2 = useCallback(
        (newCart: V2CartItem[], change: { item: V2CartItem; qty: number }) => {
            setV2Cart(newCart)
            setCartEdited(true)
            syncCartToServer(JSON.stringify(newCart))
            analytics.track(change.qty <= 0 ? 'cartItemRemoved' : 'cartItemQuantityChanged', {
                product_id: change.item.product_id,
                purchase_type: change.item.purchase_option,
                qty: change.qty
            })
        },
        [setV2Cart, setCartEdited, syncCartToServer]
    )

    const increaseV2Qty = useCallback(
        (index: number) => {
            const item = v2Cart[index]
            if (!item) return
            const updated = [...v2Cart]
            updated[index] = { ...item, qty: item.qty + 1 }
            applyV2(updated, { item, qty: item.qty + 1 })
        },
        [v2Cart, applyV2]
    )

    const decreaseV2Qty = useCallback(
        (index: number) => {
            const item = v2Cart[index]
            if (!item || item.qty <= 1) return
            const updated = [...v2Cart]
            updated[index] = { ...item, qty: item.qty - 1 }
            applyV2(updated, { item, qty: item.qty - 1 })
        },
        [v2Cart, applyV2]
    )

    const removeV2Entry = useCallback(
        (index: number) => {
            const item = v2Cart[index]
            if (!item) return
            applyV2(
                v2Cart.filter((_, i) => i !== index),
                { item, qty: 0 }
            )
        },
        [v2Cart, applyV2]
    )

    // ─── Format-item handlers ─────────────────────────────────────────────────

    const changeFormatItemQty = useCallback(
        (itemCode: string, delta: number, currentQty: number, _formatType?: 'subscription' | 'onetime') => {
            const newQty = currentQty + delta
            if (newQty < 1) return
            const v2Index = findV2IndexByItemCode(v2Cart, fgProductsV2, itemCode)
            if (v2Index >= 0) {
                if (delta > 0) increaseV2Qty(v2Index)
                else decreaseV2Qty(v2Index)
            }
        },
        [v2Cart, fgProductsV2, increaseV2Qty, decreaseV2Qty]
    )

    const removeFormatItem = useCallback(
        (itemCode: string, _formatType?: 'subscription' | 'onetime') => {
            const v2Index = findV2IndexByItemCode(v2Cart, fgProductsV2, itemCode)
            if (v2Index >= 0) removeV2Entry(v2Index)
        },
        [v2Cart, fgProductsV2, removeV2Entry]
    )

    const { error: orderSpecError } = useCartLimit({ currency: OMAN_CURRENCY })

    return {
        increaseV2Qty,
        decreaseV2Qty,
        removeV2Entry,
        changeFormatItemQty,
        removeFormatItem,
        orderSpecError
    }
}
